/**
 * Error helpers for the "preview as role" token routes (B2-0).
 *
 * The errors reaching here come from the `clients/*` layer, i.e. through
 * `rethrowApi`: `.status` (HTTP status), `.message` (the backend errorCode),
 * `.response` (the axios response, raw headers included) and `.cause` (the
 * axios error, `.code` included — `ERR_NETWORK` & co. for "no answer").
 */
import { fmtCountdown } from "./fmtCountdown";

type Maybe = {
  status?: unknown;
  message?: unknown;
  response?: { status?: unknown; headers?: Record<string, unknown> };
  cause?: { code?: unknown };
} | null | undefined;

/** Same set as `client.ts` `NO_ANSWER_CODES`: the browser reached no server. */
const NO_ANSWER_CODES = new Set(["ERR_NETWORK", "ECONNABORTED", "ETIMEDOUT"]);

export function errorStatus(err: unknown): number | undefined {
  const e = err as Maybe;
  if (typeof e?.status === "number") return e.status;
  if (typeof e?.response?.status === "number") return e.response.status;
  return undefined;
}

export function isRateLimited(err: unknown): boolean {
  return errorStatus(err) === 429;
}

/**
 * "The mint may or may not have happened": no HTTP answer at all, or a gateway
 * answer (502/503/504) that says nothing about whether the backend wrote. After
 * a superseding mint the predecessor may already be CAS-shortened to 60 s, so
 * these get one forced re-mint (see `previewStore`).
 */
export function isNoAnswerOrGateway(err: unknown): boolean {
  const status = errorStatus(err);
  if (status === 502 || status === 503 || status === 504) return true;
  if (status !== undefined) return false;
  const code = (err as Maybe)?.cause?.code;
  return typeof code === "string" && NO_ANSWER_CODES.has(code);
}

/** A refusal of THIS body (4xx other than 429): retrying it cannot help. */
export function isClientRejection(err: unknown): boolean {
  const status = errorStatus(err);
  return status !== undefined && status >= 400 && status < 500 && status !== 429;
}

/**
 * The preview mint's 400 shape / validation family (`checkPreviewRequest`, the
 * form limits, the row-filter validator, the template lookup): codes by their
 * head, as `translateErrorCode` reads them. `invalidAllowlistKey:<k>` reaches
 * the client as `validationError`; the raw head is listed for a direct caller.
 */
const DETERMINISTIC_REFUSAL_CODES = new Set([
  "tooManyRowFilters",
  "tooManyAllowlistedFields",
  "validationError",
  "invalidAllowlistKey",
  "invalidRowFilters",
  "invalidRowFilterKey",
  "previewTargetAmbiguous",
  "unknownCapability",
  "unknownBaseRole",
  "templateNotFound",
]);

/**
 * Audit r5 (store LOW-1): a refusal the SAME body meets again on every
 * instance — a 400 of the family above. Every other client rejection is no
 * verdict on the body: a 409 `hashFailed` (after the CAS, the predecessor put
 * back), a 401 / 403 / 404, an unknown 400 code. The store keeps only these
 * "refused" across an undo to the token's state while a forced re-mint is
 * owed; a transient one is sent again when the edit comes back to it.
 */
export function isDeterministicRefusal(err: unknown): boolean {
  if (errorStatus(err) !== 400) return false;
  const code = (err as Maybe)?.message;
  if (typeof code !== "string" || code === "") return false;
  const head = code.includes(":") ? code.slice(0, code.indexOf(":")) : code;
  return DETERMINISTIC_REFUSAL_CODES.has(head);
}

/**
 * The wait a 429 asks for, in seconds: the RAW `Retry-After` header, clamped to
 * 1…900 (the bucket window), 60 when absent or not a number.
 *
 * Raw on purpose: `rethrowApi` turns `0`, an HTTP date and garbage into
 * `undefined`, and Redis answers `TTL 0` in a window's last second. An HTTP
 * date or proxy garbage would otherwise have armed `setTimeout(NaN)` — an
 * immediate re-mint, a tight 429 loop.
 */
export function retryAfterSeconds(err: unknown): number {
  const headers = (err as Maybe)?.response?.headers;
  const raw = headers?.["retry-after"];
  if (raw === undefined || raw === null || String(raw).trim() === "") return 60;
  const n = Number(raw);
  if (!Number.isFinite(n)) return 60;
  return Math.min(900, Math.max(1, n));
}

/** The `errors.*` translator shape this file needs (next-intl's `t` + `has`). */
export interface ErrorsTranslator {
  (key: string): string;
  has: (key: string) => boolean;
}

/**
 * The rule the role editor's "Save to role" has always applied to a thrown
 * backend code (moved here so the re-mint shares it): a suffixed code
 * (`invalidRowFilterKey:tierr`) translates by its head; `null` when there is no
 * own copy, so the caller picks its fallback. `invalidAllowlistKey:<k>` reaches
 * the client as errorCode `validationError` (the prefix is no suffixed family
 * in `ErrorFormater`) and so shows `errors.validationError`.
 */
export function translateErrorCode(
  code: string | null | undefined,
  tErrors: ErrorsTranslator,
): string | null {
  if (!code) return null;
  const head = code.includes(":") ? code.slice(0, code.indexOf(":")) : code;
  if (!head || !tErrors.has(head)) return null;
  const translated = tErrors(head);
  return translated && translated !== head && translated !== `errors.${head}`
    ? translated
    : null;
}

/** The `rolePreview.*` translator shape the start/switch toast needs. */
export type RolePreviewTranslator = (key: string, values?: Record<string, string>) => string;

/**
 * The toast for a failed preview START or role SWITCH (LOW-15). A 429 names
 * the wait (`Retry-After`, 1:00 without one) in its own words — a switch with a
 * live predecessor draws on the re-mint bucket the edits drain, so "too many
 * starts" would be wrong there; `errors.rateLimited` ("wait a moment") does not
 * fit a wait of up to 15 minutes. Anything else stays `startError`.
 */
export function previewStartErrorText(
  err: unknown,
  t: RolePreviewTranslator,
  kind: "start" | "switch",
): string {
  if (isRateLimited(err)) {
    const countdown = fmtCountdown(retryAfterSeconds(err) * 1000);
    return t(kind === "switch" ? "switchRateLimited" : "startRateLimited", { countdown });
  }
  return t("startError");
}
